

















import {
  createComponent,
  computed,
  Ref,
  ref,
  watch,
} from '@vue/composition-api';
import { debounce } from 'lodash-es';

import {
  parseQueryString,
  addToQueryString,
  removeFromQueryString,
} from '@sbvr/common/legacy-shared/helpers/url-helper';
import UIRowIndexLoader from '@sbvr/common/legacy-shared/components/ui/UIRowIndexLoader.vue';
import WebPanelPage from '@sbvr/web-panel/WebPanelPage.vue';

import { useStore } from '@/core/store';
import Device from '@/core/store/types/Device';
import DeviceGroup from '@/core/store/types/DeviceGroup';

import DeviceIndexHeader from './DeviceIndexHeader.vue';
import DeviceIndexList from './DeviceIndexList.vue';

function useDeviceSearch (): {
  searchQuery: Ref<string>;
  searchLoading: Ref<boolean>;
  filteredDeviceGroups: Readonly<Ref<readonly DeviceGroup[]>>;
  } {
  const store = useStore();

  const queryString = parseQueryString();

  const searchQuery: Ref<string> = ref(queryString.search || '');
  const searchLoading: Ref<boolean> = ref(false);
  const filteredDeviceIds: Ref<string[] | null> = ref(null);

  async function search () {
    if (searchQuery.value.length) {
      searchLoading.value = true;

      const deviceIds: string[] = await store.dispatch(
        'searchDevices',
        searchQuery.value
      );

      filteredDeviceIds.value = deviceIds;

      addToQueryString('search', searchQuery.value);

      searchLoading.value = false;
    } else {
      removeFromQueryString('search');
      filteredDeviceIds.value = null;
    }
  }

  if (searchQuery.value.length) search();

  const debouncedCall = debounce(search, 500);
  watch(searchQuery, () => debouncedCall());

  const filteredDeviceGroups = computed((): DeviceGroup[] => {
    if (filteredDeviceIds.value) {
      return store.getters['entities/DeviceGroup/all']().reduce(
        (filteredDeviceGroups, deviceGroup) => {
          if (deviceGroup.devices) {
            const devices = deviceGroup.devices.filter(device => {
              if (filteredDeviceIds.value) { return filteredDeviceIds.value.includes(device.id); }
            });
            if (devices.length) {
              filteredDeviceGroups.push({
                ...deviceGroup,
                devices,
              });
            }
          }

          return filteredDeviceGroups;
        },
        []
      );
    } else {
      return store.getters['entities/DeviceGroup/all']();
    }
  });

  return {
    searchQuery,
    searchLoading,
    filteredDeviceGroups,
  };
}

function useDeviceGroupToggle (): { toggleDeviceGroup: (id: string) => void } {
  const store = useStore();

  async function toggleDeviceGroup (id: string): Promise<void> {
    const deviceGroup: DeviceGroup = store.getters['entities/DeviceGroup'](id);

    const _open = !deviceGroup._open;
    store.dispatch('insertEntities', {
      name: 'DeviceGroup',
      payload: {
        id,
        _open,
      },
    });

    if (_open && deviceGroup.devices.length === 0) {
      store.dispatch('insertEntities', {
        name: 'DeviceGroup',
        payload: {
          id,
          _loading: true,
        },
      });

      await new Promise(resolve => setTimeout(() => resolve(), 1000)); // TODO Add real API call

      store.dispatch('insertEntities', {
        name: 'DeviceGroup',
        payload: {
          id,
          _loading: false,
        },
      });
    }
  }

  return {
    toggleDeviceGroup,
  };
}

export default createComponent({
  props: {
    initialLoading: {
      type: Boolean,
      required: true,
    },
  },
  components: {
    WebPanelPage,
    UIRowIndexLoader,
    DeviceIndexHeader,
    DeviceIndexList,
  },
  setup () {
    return {
      ...useDeviceSearch(),
      ...useDeviceGroupToggle(),
    };
  },
});
